import { STORAGE_RECOVERY_MESSAGES } from "@econ-v1/ports";
import { boundedAcquire, } from "../concurrency/bounded-lock.js";
import { MIGRATIONS, MigrationChecksumDriftError, migrateDatabase, } from "./migrator.js";
import { classifyPersistentStorageError, OpfsUnavailableError, StorageCorruptError, StorageQuotaExceededError, StorageWriterConflictError, } from "./sqlite-runtime.js";
import { SqliteConversationRepository } from "./repositories/conversation-repository.js";
import { SqliteDurableRepositoryEventStore } from "./repositories/durable-event-store.js";
import { SqliteOutboxRepository } from "./repositories/outbox.js";
import { SqliteAgentRepository } from "./repositories/agent-repository.js";
import { SqliteGraphRepository } from "./repositories/graph-repository.js";
import { SqliteSettingsRepository } from "./repositories/settings.js";
import { SqliteRevisionedSettingsRepository } from "./repositories/settings-repository.js";
import { SqliteSyncLedgerRepository } from "./repositories/sync-ledger.js";
import { SqliteWalletMirrorRepository } from "./repositories/wallet-mirror-repository.js";
export { RESET_LOCAL_STORAGE_CONFIRMATION } from "@econ-v1/ports";
import { RESET_LOCAL_STORAGE_CONFIRMATION } from "@econ-v1/ports";
const IDENTITY_IMPORT_SOURCE = "p1-indexeddb-v1";
class IdentitySourceUnavailableError extends Error {
    constructor() {
        super("Protected identity metadata is temporarily unavailable");
        this.name = "IdentitySourceUnavailableError";
    }
}
/**
 * How long a booting client waits for a departing writer to hand the database over
 * before it reports a writer conflict, and how often it re-attempts while waiting.
 *
 * Ownership is guarded by an exclusive Web Lock plus the OPFS SAH pool's access
 * handles. Both are attached to the document's dedicated storage worker, and both are
 * released *asynchronously* once that document goes away. A reload therefore starts the
 * replacement document while the outgoing worker still owns the database, so the first
 * acquisition attempt can lose a race that resolves itself within a few hundred
 * milliseconds. The wait stays far below the recovery-state poll windows so a genuinely
 * concurrent second writer still surfaces `writer-conflict` promptly.
 */
const DEFAULT_WRITER_HANDOVER_TIMEOUT_MS = 2_000;
const DEFAULT_WRITER_HANDOVER_RETRY_INTERVAL_MS = 50;
/**
 * Takes the exclusive writer Web Lock, tolerating the ownership handover window a
 * departing document leaves behind.
 *
 * A writer conflict is only terminal while another *live* client holds the database.
 * When the document that owned the storage worker is reloaded or navigated away from,
 * that worker — and with it the exclusive Web Lock and the OPFS access handles — is torn
 * down asynchronously, after the replacement document has already begun booting. A single
 * zero-wait attempt therefore latches an unrecoverable `writer-conflict` whose cause has
 * already disappeared. `boundedAcquire` re-attempts until the handover deadline closes
 * that gap without weakening the guarantee: a second writer that is genuinely still
 * running keeps the lock for the whole window, `boundedAcquire` reports it unavailable,
 * and this lease surfaces `writer-conflict` exactly as a single attempt would have.
 */
export class BrowserWriterLease {
    #handover;
    #locks;
    constructor(locks = globalThis.navigator?.locks, handover = {}) {
        this.#handover = handover;
        this.#locks = locks;
    }
    async acquire(name) {
        if (this.#locks === undefined)
            throw new OpfsUnavailableError();
        const outcome = await boundedAcquire(this.#locks, `node-client-sqlite-writer:${name}`, {
            delay: this.#handover.delay,
            elapsed: this.#handover.elapsed,
            mode: "exclusive",
            retryIntervalMs: this.#handover.retryIntervalMs ?? DEFAULT_WRITER_HANDOVER_RETRY_INTERVAL_MS,
            timeoutMs: this.#handover.timeoutMs ?? DEFAULT_WRITER_HANDOVER_TIMEOUT_MS,
        });
        if (!outcome.acquired)
            throw new StorageWriterConflictError();
        return outcome.release;
    }
}
export class InMemoryWriterLease {
    static #held = new Set();
    static clearForTests() {
        this.#held.clear();
    }
    async acquire(name) {
        if (InMemoryWriterLease.#held.has(name))
            throw new StorageWriterConflictError();
        InMemoryWriterLease.#held.add(name);
        return async () => { InMemoryWriterLease.#held.delete(name); };
    }
}
export class OpfsDatabaseStorage {
    #crypto;
    #deleteDatabase;
    #filename;
    #identityMetadata;
    #lease;
    #migrations;
    #now;
    #runtimeFactory;
    #operationQueue = Promise.resolve();
    #releaseLease;
    #runtime;
    #storageGeneration = 0;
    #state = { schemaVersion: 0, status: "starting" };
    repositories = {
        agents: {
            apply: async (entity) => await this.#repositoryOperation(async (database) => (await new SqliteAgentRepository(database, { now: this.#now }).apply(entity))),
            command: async (input) => await this.#repositoryOperation(async (database) => (await new SqliteAgentRepository(database, { now: this.#now }).command(input))),
            list: async () => await this.#repositoryOperation(async (database) => (await new SqliteAgentRepository(database, { now: this.#now }).list())),
            read: async (id) => await this.#repositoryOperation(async (database) => (await new SqliteAgentRepository(database, { now: this.#now }).read(id))),
        },
        events: {
            listUndelivered: async () => await this.#repositoryOperation(async (database) => await new SqliteDurableRepositoryEventStore(database).listUndelivered()),
            markDelivered: async (sequence, deliveredAt) => await this.#repositoryOperation(async (database) => (await new SqliteDurableRepositoryEventStore(database).markDelivered(sequence, deliveredAt))),
            markDeliveryAttempt: async (sequence) => await this.#repositoryOperation(async (database) => (await new SqliteDurableRepositoryEventStore(database).markDeliveryAttempt(sequence))),
        },
        conversations: {
            acknowledgeMessage: async (input) => await this.#repositoryOperation(async (database) => (await new SqliteConversationRepository(database, { now: this.#now }).acknowledgeMessage(input))),
            appendPendingMessage: (input) => ({
                commit: async () => await this.#repositoryOperation(async (database) => (await new SqliteConversationRepository(database, { now: this.#now }).appendPendingMessage(input).commit())),
            }),
            applyDelta: (delta) => ({
                commit: async () => await this.#repositoryOperation(async (database) => (await new SqliteConversationRepository(database, { now: this.#now }).applyDelta(delta).commit())),
            }),
            failMessage: async (input) => await this.#repositoryOperation(async (database) => (await new SqliteConversationRepository(database, { now: this.#now }).failMessage(input))),
            listConversations: async (params) => await this.#repositoryOperation(async (database) => (await new SqliteConversationRepository(database, { now: this.#now }).listConversations(params))),
            listMessages: async (conversationId, params) => await this.#repositoryOperation(async (database) => (await new SqliteConversationRepository(database, { now: this.#now }).listMessages(conversationId, params))),
        },
        outbox: {
            create: async (input) => await this.#repositoryOperation(async (database) => (await new SqliteOutboxRepository(database, { now: this.#now }).create(input).commit())),
            delete: async (id) => await this.#repositoryOperation(async (database) => (await new SqliteOutboxRepository(database, { now: this.#now }).delete(id).commit())),
            list: async (filter) => await this.#repositoryOperation(async (database) => (await new SqliteOutboxRepository(database, { now: this.#now }).list(filter))),
            read: async (id) => await this.#repositoryOperation(async (database) => (await new SqliteOutboxRepository(database, { now: this.#now }).read(id))),
            updateState: async (id, state) => await this.#repositoryOperation(async (database) => (await new SqliteOutboxRepository(database, { now: this.#now }).updateState(id, state).commit())),
        },
        settings: {
            delete: async (key) => await this.#repositoryOperation(async (database) => (await new SqliteSettingsRepository(database, { now: this.#now }).delete(key).commit())),
            list: async () => await this.#repositoryOperation(async (database) => (await new SqliteSettingsRepository(database, { now: this.#now }).list())),
            read: async (key) => await this.#repositoryOperation(async (database) => (await new SqliteSettingsRepository(database, { now: this.#now }).read(key))),
            set: async (input) => await this.#repositoryOperation(async (database) => (await new SqliteSettingsRepository(database, { now: this.#now }).set(input).commit())),
        },
        revisionedSettings: {
            apply: async (entity) => await this.#repositoryOperation(async (database) => (await new SqliteRevisionedSettingsRepository(database, { now: this.#now }).apply(entity))),
            command: async (input) => await this.#repositoryOperation(async (database) => (await new SqliteRevisionedSettingsRepository(database, { now: this.#now }).command(input))),
            list: async () => await this.#repositoryOperation(async (database) => (await new SqliteRevisionedSettingsRepository(database, { now: this.#now }).list())),
            read: async (id) => await this.#repositoryOperation(async (database) => (await new SqliteRevisionedSettingsRepository(database, { now: this.#now }).read(id))),
        },
        notifications: {
            applyDelta: (delta) => ({
                commit: async () => await this.#repositoryOperation(async (database) => (await new SqliteConversationRepository(database, { now: this.#now }).applyDelta(delta).commit())),
            }),
            listNotifications: async (params) => await this.#repositoryOperation(async (database) => (await new SqliteConversationRepository(database, { now: this.#now }).listNotifications(params))),
            markRead: (input) => ({
                commit: async () => await this.#repositoryOperation(async (database) => (await new SqliteConversationRepository(database, { now: this.#now }).markRead(input).commit())),
            }),
        },
        graphs: {
            apply: async (entity) => await this.#repositoryOperation(async (database) => (await new SqliteGraphRepository(database, { now: this.#now }).apply(entity))),
            command: async (input) => await this.#repositoryOperation(async (database) => (await new SqliteGraphRepository(database, { now: this.#now }).command(input))),
            list: async () => await this.#repositoryOperation(async (database) => (await new SqliteGraphRepository(database, { now: this.#now }).list())),
            read: async (id) => await this.#repositoryOperation(async (database) => (await new SqliteGraphRepository(database, { now: this.#now }).read(id))),
        },
        syncLedger: {
            delete: async (domain) => await this.#repositoryOperation(async (database) => (await new SqliteSyncLedgerRepository(database, { now: this.#now }).delete(domain).commit())),
            list: async () => await this.#repositoryOperation(async (database) => (await new SqliteSyncLedgerRepository(database, { now: this.#now }).list())),
            read: async (domain) => await this.#repositoryOperation(async (database) => (await new SqliteSyncLedgerRepository(database, { now: this.#now }).read(domain))),
            setCursor: async (input) => await this.#repositoryOperation(async (database) => (await new SqliteSyncLedgerRepository(database, { now: this.#now }).setCursor(input).commit())),
        },
        wallet: {
            readCommandResult: async (idempotencyKey) => await this.#repositoryOperation(async (database) => (await new SqliteWalletMirrorRepository(database, { now: this.#now }).readCommandResult(idempotencyKey))),
            readSnapshot: async () => await this.#repositoryOperation(async (database) => (await new SqliteWalletMirrorRepository(database, { now: this.#now }).readSnapshot())),
            writeCommandResult: async (idempotencyKey, result) => await this.#repositoryOperation(async (database) => {
                await new SqliteWalletMirrorRepository(database, { now: this.#now }).writeCommandResult(idempotencyKey, result);
            }),
            writeSnapshot: async (snapshot) => await this.#repositoryOperation(async (database) => {
                await new SqliteWalletMirrorRepository(database, { now: this.#now }).writeSnapshot(snapshot);
            }),
        },
    };
    constructor(options) {
        this.#crypto = options.crypto ?? globalThis.crypto;
        this.#deleteDatabase = options.deleteDatabase;
        this.#filename = options.filename;
        this.#identityMetadata = options.identityMetadata;
        this.#lease = options.lease;
        this.#migrations = options.migrations ?? MIGRATIONS;
        this.#now = options.now ?? (() => new Date().toISOString());
        this.#runtimeFactory = options.runtimeFactory;
    }
    initialize() {
        return this.#serialize(async () => {
            if (this.#state.status === "ready")
                return this.#state;
            return await this.#initialize();
        });
    }
    recoveryState() {
        return this.#serialize(async () => this.#state);
    }
    refreshIdentityMetadata(snapshot) {
        return this.#serialize(async () => await this.#refreshIdentityMetadata(snapshot));
    }
    async #refreshIdentityMetadata(snapshot) {
        if (this.#runtime === undefined)
            return;
        const current = snapshot ?? await this.#identityMetadata();
        if (current === undefined)
            return;
        if (hasFailedIdentitySource(current)) {
            this.#recover("identity-source-unavailable", STORAGE_RECOVERY_MESSAGES["identity-source-unavailable"], schemaVersion(this.#runtime), true);
            return;
        }
        try {
            writeIdentityMetadata(this.#runtime, current, this.#now);
        }
        catch (error) {
            const classified = classifyPersistentStorageError(error, "migration-failed");
            const code = recoveryCode(classified);
            this.#recover(code, STORAGE_RECOVERY_MESSAGES[code], schemaVersion(this.#runtime), true);
        }
    }
    retry() {
        return this.#serialize(async () => {
            const cleanupFailure = await this.#releaseOwnership();
            if (cleanupFailure !== undefined) {
                const classified = classifyPersistentStorageError(cleanupFailure, "opfs-unavailable");
                const code = recoveryCode(classified);
                return this.#recover(code, STORAGE_RECOVERY_MESSAGES[code], this.#state.schemaVersion, false);
            }
            this.#state = {
                schemaVersion: this.#state.schemaVersion,
                ...(this.#storageGeneration === 0 ? {} : { storageGeneration: this.#storageGeneration }),
                status: "starting",
            };
            return await this.#initialize();
        });
    }
    exportEncryptedDiagnostic(options) {
        return this.#serialize(async () => await this.#exportEncryptedDiagnostic(options));
    }
    async #exportEncryptedDiagnostic(options) {
        if (this.#runtime === undefined)
            throw new Error("No SQLite database is available to export");
        let plaintext;
        try {
            plaintext = this.#runtime.exportDatabase();
        }
        catch (error) {
            const classified = classifyPersistentStorageError(error, "corrupt");
            const code = recoveryCode(classified);
            this.#recover(code, STORAGE_RECOVERY_MESSAGES[code], schemaVersion(this.#runtime), true);
            throw new Error("Storage diagnostic export failed");
        }
        try {
            const iv = this.#crypto.getRandomValues(new Uint8Array(12));
            const ciphertext = await this.#crypto.subtle.encrypt({ iv, name: "AES-GCM" }, options.key, plaintext);
            return {
                algorithm: "AES-GCM",
                ciphertextBase64: base64(new Uint8Array(ciphertext)),
                createdAt: this.#now(),
                format: "node-client-sqlite-diagnostic-v1",
                ivBase64: base64(iv),
                kdf: options.kdf,
            };
        }
        catch {
            this.#recover("diagnostic-failed", STORAGE_RECOVERY_MESSAGES["diagnostic-failed"], schemaVersion(this.#runtime), true);
            throw new Error("Storage diagnostic export failed");
        }
        finally {
            plaintext.fill(0);
        }
    }
    reset(options) {
        return this.#serialize(async () => await this.#reset(options));
    }
    async #reset(options) {
        if (options.confirmation !== RESET_LOCAL_STORAGE_CONFIRMATION) {
            throw new TypeError("Local storage reset confirmation does not match");
        }
        const runtime = this.#runtime;
        let resetFailure;
        if (this.#releaseLease === undefined) {
            try {
                this.#releaseLease = await this.#lease.acquire(this.#filename);
            }
            catch (error) {
                resetFailure = error;
            }
        }
        if (resetFailure === undefined) {
            try {
                await runtime?.close();
                this.#runtime = undefined;
            }
            catch (error) {
                resetFailure = error;
            }
        }
        if (resetFailure === undefined) {
            try {
                if (this.#deleteDatabase !== undefined)
                    await this.#deleteDatabase();
                else if (runtime !== undefined)
                    await runtime.wipe();
                this.#storageGeneration += 1;
            }
            catch (error) {
                resetFailure = error;
            }
        }
        if (this.#runtime === undefined) {
            try {
                await this.#releaseLease?.();
            }
            catch (error) {
                resetFailure ??= error;
            }
            finally {
                this.#releaseLease = undefined;
            }
        }
        if (resetFailure !== undefined) {
            const classified = classifyPersistentStorageError(resetFailure, "opfs-unavailable");
            const code = recoveryCode(classified);
            return this.#recover(code, STORAGE_RECOVERY_MESSAGES[code], schemaVersion(this.#runtime), false);
        }
        this.#state = {
            schemaVersion: 0,
            storageGeneration: this.#storageGeneration,
            status: "starting",
        };
        return await this.#initialize();
    }
    close() {
        return this.#serialize(async () => await this.#close());
    }
    async #close() {
        const cleanupFailure = await this.#releaseOwnership();
        if (cleanupFailure !== undefined) {
            const classified = classifyPersistentStorageError(cleanupFailure, "opfs-unavailable");
            const code = recoveryCode(classified);
            this.#recover(code, STORAGE_RECOVERY_MESSAGES[code], this.#state.schemaVersion, false);
        }
    }
    #serialize(operation) {
        const result = this.#operationQueue.then(operation);
        this.#operationQueue = result.then(() => undefined, () => undefined);
        return result;
    }
    #repositoryOperation(operation) {
        return this.#serialize(async () => {
            if (this.#state.status !== "ready" || this.#runtime === undefined) {
                throw new Error("Storage is not ready");
            }
            try {
                return await operation(this.#runtime.database);
            }
            catch (error) {
                const classified = classifyPersistentStorageError(error, "migration-failed");
                if (isPersistentStorageFailure(classified)) {
                    const code = recoveryCode(classified);
                    this.#recover(code, STORAGE_RECOVERY_MESSAGES[code], schemaVersion(this.#runtime), true);
                }
                throw classified;
            }
        });
    }
    async #initialize() {
        let phase = "open";
        try {
            const runtime = await this.#acquireOwnership();
            phase = "migration";
            const migration = await migrateDatabase(runtime.database, this.#migrations, { now: this.#now });
            phase = "identity";
            await reconcileLegacyIdentityMetadata(runtime, this.#identityMetadata, this.#now);
            phase = "integrity";
            const integrity = runtime.database.selectValue("PRAGMA integrity_check");
            if (integrity !== "ok") {
                return this.#recover("corrupt", STORAGE_RECOVERY_MESSAGES.corrupt, migration.version, true);
            }
            this.#state = {
                schemaVersion: migration.version,
                ...(this.#storageGeneration === 0 ? {} : { storageGeneration: this.#storageGeneration }),
                status: "ready",
            };
            return this.#state;
        }
        catch (error) {
            const classified = classifyPersistentStorageError(error, phase === "open" ? "opfs-unavailable" : phase === "integrity" ? "corrupt" : "migration-failed");
            const code = recoveryCode(classified);
            const exportDiagnostic = this.#runtime !== undefined;
            if (!exportDiagnostic) {
                await this.#releaseOwnership();
            }
            return this.#recover(code, STORAGE_RECOVERY_MESSAGES[code], schemaVersion(this.#runtime), exportDiagnostic);
        }
    }
    /**
     * Takes the writer lease and opens the SQLite runtime. The lease itself
     * ({@link BrowserWriterLease}) tolerates the ownership-handover window left behind by a
     * departing document — it only raises a `writer-conflict` once a genuinely concurrent
     * writer has held the lock for the whole handover deadline — so this step performs a
     * single acquisition and lets a real conflict propagate. Both assignments are
     * idempotent, so a retry driven by `retry()` only redoes the step that failed.
     */
    async #acquireOwnership() {
        this.#releaseLease ??= await this.#lease.acquire(this.#filename);
        this.#runtime ??= await this.#runtimeFactory();
        return this.#runtime;
    }
    async #releaseOwnership() {
        const runtime = this.#runtime;
        const releaseLease = this.#releaseLease;
        try {
            await runtime?.close();
        }
        catch (error) {
            return error;
        }
        this.#runtime = undefined;
        this.#releaseLease = undefined;
        try {
            await releaseLease?.();
        }
        catch (error) {
            return error;
        }
        return undefined;
    }
    #recover(code, message, schemaVersionValue, exportDiagnostic) {
        this.#state = {
            actions: { exportDiagnostic, reset: true, retry: true },
            code,
            message,
            schemaVersion: schemaVersionValue,
            ...(this.#storageGeneration === 0 ? {} : { storageGeneration: this.#storageGeneration }),
            status: "recovery-required",
        };
        return this.#state;
    }
}
function isPersistentStorageFailure(error) {
    return error instanceof OpfsUnavailableError ||
        error instanceof StorageCorruptError ||
        error instanceof StorageQuotaExceededError ||
        error instanceof StorageWriterConflictError;
}
async function reconcileLegacyIdentityMetadata(runtime, readSnapshot, now) {
    const snapshot = await readSnapshot();
    if (snapshot === undefined)
        return;
    if (hasFailedIdentitySource(snapshot))
        throw new IdentitySourceUnavailableError();
    writeIdentityMetadata(runtime, snapshot, now);
}
function writeIdentityMetadata(runtime, snapshot, now) {
    const importedAt = now();
    runtime.database.transaction("IMMEDIATE", (database) => {
        const identityAlreadyImported = database.selectValue("SELECT COUNT(*) FROM identity_import_state WHERE source = ?", [IDENTITY_IMPORT_SOURCE]) === 1;
        let changed = false;
        const existingIdentity = database.selectObject(`SELECT custody_mode, did, node_id, public_key_multibase
        FROM device_identity_metadata WHERE id = 1`);
        if (snapshot.publicIdentity.status === "absent") {
            if (existingIdentity !== undefined) {
                database.exec("DELETE FROM device_identity_metadata WHERE id = 1");
                changed = true;
            }
        }
        else if (snapshot.publicIdentity.status === "present") {
            const identity = snapshot.publicIdentity.value;
            if (!recordMatches(existingIdentity, {
                custody_mode: identity.custodyMode,
                did: identity.did,
                node_id: identity.nodeId,
                public_key_multibase: identity.publicKeyMultibase,
            })) {
                database.exec(`INSERT OR REPLACE INTO device_identity_metadata
            (id, custody_mode, did, node_id, public_key_multibase, imported_at)
            VALUES (1, ?, ?, ?, ?, ?)`, { bind: [identity.custodyMode, identity.did, identity.nodeId, identity.publicKeyMultibase, importedAt] });
                changed = true;
            }
        }
        const existingDelegation = database.selectObject(`SELECT revocation_id, scopes_json, issued_at, expires_at
        FROM delegation_metadata WHERE id = 1`);
        if (snapshot.delegation.status === "absent") {
            if (existingDelegation !== undefined) {
                database.exec("DELETE FROM delegation_metadata WHERE id = 1");
                changed = true;
            }
        }
        else if (snapshot.delegation.status === "present") {
            const delegation = snapshot.delegation.value;
            const mirroredDelegation = {
                expires_at: delegation.expiresAt,
                issued_at: delegation.issuedAt,
                revocation_id: delegation.revocationId,
                scopes_json: JSON.stringify(delegation.scopes),
            };
            if (!recordMatches(existingDelegation, mirroredDelegation)) {
                database.exec(`INSERT OR REPLACE INTO delegation_metadata
            (id, revocation_id, scopes_json, issued_at, expires_at, imported_at)
            VALUES (1, ?, ?, ?, ?, ?)`, { bind: [
                        delegation.revocationId,
                        mirroredDelegation.scopes_json,
                        delegation.issuedAt,
                        delegation.expiresAt,
                        importedAt,
                    ] });
                changed = true;
            }
        }
        const existingCandidates = database.selectObjects(`SELECT address, kind, state, last_connected_at, lease_expires_at
        FROM home_node_candidates ORDER BY address`);
        const desiredCandidates = snapshot.connectionMetadata.status === "present"
            ? candidateRecords(snapshot.connectionMetadata.value)
            : [];
        if (!recordsMatch(existingCandidates, desiredCandidates)) {
            database.exec("DELETE FROM home_node_candidates");
            for (const candidate of desiredCandidates) {
                database.exec(`INSERT INTO home_node_candidates
            (address, kind, state, last_connected_at, lease_expires_at, imported_at)
            VALUES (?, ?, 'active', ?, NULL, ?)`, { bind: [candidate.address, candidate.kind, candidate.last_connected_at, importedAt] });
            }
            changed = true;
        }
        const importMarkerMissing = !identityAlreadyImported && hasIdentityMetadata(snapshot);
        if (changed || importMarkerMissing) {
            database.exec(`INSERT INTO identity_import_state(source, imported_at) VALUES (?, ?)
          ON CONFLICT(source) DO UPDATE SET imported_at = excluded.imported_at`, { bind: [IDENTITY_IMPORT_SOURCE, importedAt] });
        }
    });
}
function candidateRecords(metadata) {
    const candidates = new Map();
    if (metadata.relayUrl !== undefined) {
        candidates.set(metadata.relayUrl, {
            address: metadata.relayUrl,
            kind: "relay",
            last_connected_at: metadata.lastConnectedAt ?? null,
            lease_expires_at: null,
            state: "active",
        });
    }
    for (const address of [metadata.wanOrigin, ...metadata.directAddresses]) {
        if (address === undefined)
            continue;
        candidates.set(address, {
            address,
            kind: "direct",
            last_connected_at: metadata.lastConnectedAt ?? null,
            lease_expires_at: null,
            state: "active",
        });
    }
    return [...candidates.values()].sort((left, right) => left.address.localeCompare(right.address));
}
function recordsMatch(actual, expected) {
    return actual.length === expected.length &&
        actual.every((record, index) => recordMatches(record, expected[index]));
}
function recordMatches(actual, expected) {
    return actual !== undefined && Object.entries(expected).every(([key, value]) => actual[key] === value);
}
function hasFailedIdentitySource(snapshot) {
    return snapshot.publicIdentity.status === "failed" ||
        snapshot.delegation.status === "failed" ||
        snapshot.connectionMetadata.status === "failed";
}
function hasIdentityMetadata(snapshot) {
    return snapshot.publicIdentity.status === "present" ||
        snapshot.delegation.status === "present" ||
        snapshot.connectionMetadata.status === "present";
}
function recoveryCode(error) {
    if (error instanceof IdentitySourceUnavailableError)
        return "identity-source-unavailable";
    if (error instanceof MigrationChecksumDriftError)
        return "checksum-drift";
    if (error instanceof OpfsUnavailableError)
        return "opfs-unavailable";
    if (error instanceof StorageCorruptError)
        return "corrupt";
    if (error instanceof StorageQuotaExceededError)
        return "quota-exceeded";
    if (error instanceof StorageWriterConflictError)
        return "writer-conflict";
    return "migration-failed";
}
function schemaVersion(runtime) {
    if (runtime === undefined)
        return 0;
    try {
        return Number(runtime.database.selectValue("SELECT COALESCE(MAX(version), 0) FROM schema_migrations") ?? 0);
    }
    catch {
        return 0;
    }
}
function base64(bytes) {
    let binary = "";
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    return btoa(binary);
}
//# sourceMappingURL=opfs-database.js.map